import el from "@cypherpotato/el"
import { getReranker } from "../rerankers";
import { formatFloat, formatNumber } from "../ui/format";
import { Icon } from "./icon";
import { ToggleButton } from "./button";

export function GatewayPipeline(gatewayData, rerankers) {
    if (!gatewayData) {
        return el(".gateway-pipeline")
    }

    const { parameters } = gatewayData;
    let dragState = null;

    const handleWindowMouseMove = (event) => {
        if (!dragState) {
            return;
        }

        const deltaX = event.clientX - dragState.lastClientX;
        dragState.container.scrollLeft -= deltaX;
        dragState.lastClientX = event.clientX;
    };

    const endDrag = () => {
        if (!dragState) {
            return;
        }

        dragState.container.classList.remove("is-dragging");
        window.removeEventListener("mousemove", handleWindowMouseMove);
        window.removeEventListener("mouseup", handleWindowMouseUp);
        dragState = null;
    };

    const handleWindowMouseUp = (event) => {
        if (event.button !== 0) {
            return;
        }

        endDrag();
    };

    const handleMouseDown = (event) => {
        if (event.button !== 0) {
            return;
        }

        const interactiveElement = event.target.closest("button, a, input, textarea, select, [data-no-drag]");
        if (interactiveElement) {
            return;
        }

        dragState = {
            container: event.currentTarget,
            lastClientX: event.clientX,
        };

        dragState.container.classList.add("is-dragging");
        window.addEventListener("mousemove", handleWindowMouseMove);
        window.addEventListener("mouseup", handleWindowMouseUp);
        event.preventDefault();
    };

    return el(".gateway-pipeline-wrapper",
        el(".gateway-pipeline-toolbar",
            ToggleButton(Icon("eye-off-line"), "Hide disabled steps", {
                onToggle(event) {
                    event.target.closest(".gateway-pipeline-wrapper").classList.toggle("hide-disabled-steps", event.value);
                }
            })
        ),
        el(".gateway-pipeline",
            {
                onMouseDown: handleMouseDown,
                onMouseUp: handleWindowMouseUp,
                onMouseLeave(event) {
                    if (!dragState) {
                        return;
                    }

                    if (!event.buttons) {
                        endDrag();
                    }
                },
            },
            el(".pipeline-column",
                el(".column-title", "Input"),
                el(".pipeline-step.hidden-step",
                    { dataState: true }
                ),
            ),

            el(".pipeline-column",
                el(".column-title", Icon("pencil-ruler-2-line"), "Tools"),
                el(".pipeline-step",
                    { dataState: !!parameters.mcpSources?.length },
                    el(".step-title", "MCP sources"),
                    !!parameters.mcpSources?.length && el.fragment(
                        el(".step-param", "Count", el("span", formatNumber(parameters.mcpSources.length)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: !!parameters.protocolFunctionSources?.length },
                    el(".step-title", "Protocol Functions"),
                    !!parameters.protocolFunctionSources?.length && el.fragment(
                        el(".step-param", "Count", el("span", formatNumber(parameters.protocolFunctionSources.length)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: !!parameters.tools?.length },
                    el(".step-title", "Raw tools"),
                    !!parameters.tools?.length && el.fragment(
                        el(".step-param", "Count", el("span", formatNumber(parameters.tools.length)))
                    )
                ),
                el(".pipeline-step-group",
                    el(".group-title", "Built-in functions"),
                    el(".pipeline-step",
                        { dataState: !!parameters.sentinelOptions?.enabledFunctions?.includes("WebSearch") },
                        el(".step-title", "Web-search"),
                        !!parameters.sentinelOptions?.enabledFunctions?.includes("WebSearch") && el.fragment(
                            el(".step-param", "Mode", el("span", parameters.builtinFunctionsOptions?.webSearchMode)),
                            el(".step-param", "Max results", el("span", parameters.builtinFunctionsOptions?.webSearchMaxResults)),
                        )
                    ),
                    el(".pipeline-step",
                        { dataState: !!parameters.sentinelOptions?.enabledFunctions?.includes("AdvancedWebUsage") },
                        el(".step-title", "Advanced Web-search")
                    ),
                    el(".pipeline-step",
                        { dataState: !!parameters.sentinelOptions?.enabledFunctions?.includes("XPostsSearch") },
                        el(".step-title", "X-Tools")
                    ),
                    el(".pipeline-step",
                        { dataState: !!parameters.sentinelOptions?.enabledFunctions?.includes("OpenUrl") },
                        el(".step-title", "Open URLs")
                    ),
                    el(".pipeline-step",
                        { dataState: !!parameters.sentinelOptions?.enabledFunctions?.includes("Code") },
                        el(".step-title", "Code evaluation")
                    ),
                    el(".pipeline-step",
                        { dataState: !!parameters.sentinelOptions?.enabledFunctions?.includes("ImageGeneration") },
                        el(".step-title", "Image generation"),
                        !!parameters.sentinelOptions?.enabledFunctions?.includes("ImageGeneration") && el.fragment(
                            el(".step-param", "Quality", el("span", parameters.builtinFunctionsOptions?.imageGenerationQuality)),
                            el(".step-param", "Images per call", el("span", parameters.builtinFunctionsOptions?.imageGenerationMaxResults)),
                            el(".step-param", "Mature content", el("span", parameters.builtinFunctionsOptions?.imageGenerationAllowMatureContent ? "Allow" : "Disallow")),
                        )
                    ),
                    el(".pipeline-step",
                        { dataState: !!parameters.sentinelOptions?.enabledFunctions?.includes("Remember") },
                        el(".step-title", "Memory")
                    ),
                    el(".pipeline-step",
                        { dataState: !!parameters.sentinelOptions?.enabledFunctions?.includes("GenerateDocument") },
                        el(".step-title", "Generate PDFs")
                    ),
                    el(".pipeline-step",
                        { dataState: !!parameters.sentinelOptions?.enabledFunctions?.includes("GenerateWebPage") },
                        el(".step-title", "Generate web pages")
                    ),
                )
            ),
            el(".pipeline-column",
                el(".column-title", Icon("database-2-line"), "RAG"),
                el(".pipeline-step",
                    { dataState: !!parameters.knowledgeCollections?.length },
                    el(".step-title", "Collections"),
                    !!parameters.knowledgeCollections?.length && el.fragment(
                        el(".step-param", "Count", el("span", formatNumber(parameters.knowledgeCollections.length)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: !!parameters.queryStrategy && !!parameters.knowledgeCollections?.length },
                    el(".step-title", "Query strategy"),
                    !!parameters.queryStrategy && !!parameters.knowledgeCollections?.length && el.fragment(
                        el(".step-param", "Strategy", el("span", parameters.queryStrategy))
                    )
                ),
                el(".pipeline-step",
                    { dataState: !!parameters.rerankerName && !!parameters.knowledgeCollections?.length },
                    el(".step-title", "Reranker"),
                    !!parameters.rerankerName && !!parameters.knowledgeCollections?.length && el.fragment(
                        el(".step-param", "Mode", el("span", getReranker(rerankers, parameters.rerankerName)?.name || parameters.rerankerName))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.knowledgeBaseMaximumResults != null && !!parameters.knowledgeCollections?.length },
                    el(".step-title", "Maximum results"),
                    parameters.knowledgeBaseMaximumResults != null && !!parameters.knowledgeCollections?.length && el.fragment(
                        el(".step-param", "Value", el("span", formatNumber(parameters.knowledgeBaseMaximumResults)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.knowledgeBaseMinimumScore != null && !!parameters.knowledgeCollections?.length },
                    el(".step-title", "Minimum score"),
                    parameters.knowledgeBaseMinimumScore != null && !!parameters.knowledgeCollections?.length && el.fragment(
                        el(".step-param", "Value", el("span", formatFloat(parameters.knowledgeBaseMinimumScore)))
                    )
                )
            ),
            el(".pipeline-column",
                el(".column-title", Icon("equalizer-3-line"), "Sampling and inference"),
                el(".pipeline-step",
                    { dataState: parameters.temperature != null },
                    el(".step-title", "Temperature"),
                    parameters.temperature != null && el.fragment(
                        el(".step-param", "Value", el("span", formatFloat(parameters.temperature)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.topP != null },
                    el(".step-title", "Top_P"),
                    parameters.topP != null && el.fragment(
                        el(".step-param", "Value", el("span", formatFloat(parameters.topP)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.presencePenalty != null },
                    el(".step-title", "Presence Penalty"),
                    parameters.presencePenalty != null && el.fragment(
                        el(".step-param", "Value", el("span", formatFloat(parameters.presencePenalty)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.frequencyPenalty != null },
                    el(".step-title", "Frequency Penalty"),
                    parameters.frequencyPenalty != null && el.fragment(
                        el(".step-param", "Value", el("span", formatFloat(parameters.frequencyPenalty)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.reasoningEffort != null },
                    el(".step-title", "Reasoning Effort"),
                    parameters.reasoningEffort != null && el.fragment(
                        el(".step-param", "Value", el("span", formatFloat(parameters.reasoningEffort)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.verbosity != null },
                    el(".step-title", "Verbosity"),
                    parameters.verbosity != null && el.fragment(
                        el(".step-param", "Value", el("span", formatFloat(parameters.verbosity)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.maxCompletionTokens != null },
                    el(".step-title", "Max Tokens"),
                    parameters.maxCompletionTokens != null && el.fragment(
                        el(".step-param", "Value", el("span", formatNumber(parameters.maxCompletionTokens)))
                    )
                )
            ),
            el(".pipeline-column",
                el(".column-title", Icon("presentation-line"), "Instructions and skills"),
                el(".pipeline-step",
                    { dataState: !!parameters.systemInstruction },
                    el(".step-title", "System instructions")
                ),
                el(".pipeline-step",
                    { dataState: !!parameters.systemInstructionsSources?.length },
                    el(".step-title", "External Sys. Instructions"),
                    !!parameters.systemInstructionsSources?.length && el.fragment(
                        el(".step-param", "Count", el("span", formatNumber(parameters.systemInstructionsSources.length)))
                    )
                ),
                el(".pipeline-step",
                    { dataState: !!parameters.skills?.length },
                    el(".step-title", "Skills"),
                    !!parameters.skills?.length && el.fragment(
                        el(".step-param", "Count", el("span", formatNumber(parameters.skills.length)))
                    )
                )
            ),
            // 
            el(".pipeline-column",
                el(".column-title", Icon("flag-2-line"), "Prefilling and stopping"),
                el(".pipeline-step",
                    { dataState: !!parameters.assistantPrefill },
                    el(".step-title", "Assistant Prefill")
                ),
                el(".pipeline-step",
                    { dataState: !!parameters.stop },
                    el(".step-title", "Stop")
                ),
                el(".pipeline-step",
                    { dataState: !!parameters.userPromptTemplate },
                    el(".step-title", "Prompt Template")
                ),
                el(".pipeline-step",
                    { dataState: !!parameters.includePrefillingInMessages },
                    el(".step-title", "Include prefilling")
                )
            ),
            el(".pipeline-column",
                el(".column-title", Icon("scissors-cut-line"), "Truncating"),
                el(".pipeline-step",
                    { dataState: parameters.contextMaximumSize != null },
                    el(".step-title", "Context length"),
                    parameters.contextMaximumSize != null && el.fragment(
                        el(".step-param", "Max Size", el("span", formatNumber(parameters.contextMaximumSize))),
                        el(".step-param", "Overflow action", el("span", parameters.contextOverflowAction)),
                    )
                ),
                el(".pipeline-step",
                    { dataState: parameters.toolContextCount != null },
                    el(".step-title", "Max tool count"),
                    parameters.toolContextCount != null && el.fragment(
                        el(".step-param", "Value", el("span", formatNumber(parameters.toolContextCount)))
                    )
                ),
            ),
            el(".pipeline-column",
                el(".column-title", Icon("shield-line"), "Moderation"),
                el(".pipeline-step",
                    { dataState: Object.values(parameters.moderationParameters || {}).some(v => typeof v === "number" && v > 0) },
                    el(".step-title", "Moderation"),
                    Object.values(parameters.moderationParameters || {}).some(v => typeof v === "number" && v > 0) && el(".step-param",
                        "Mode", el("span", parameters.moderationParameters?.mode || "LayeredGuardrail")),
                    !!parameters.moderationParameters?.additionalRules && el(".step-param",
                        "Additional rules", el("span", "Configured")),
                    !!parameters.moderationParameters?.violenceThreshold && el(".step-param",
                        "Violence", el("span", parameters.moderationParameters?.violenceThreshold)),
                    !!parameters.moderationParameters?.sexualExplicitThreshold && el(".step-param",
                        "Sexual/explicit", el("span", parameters.moderationParameters?.sexualExplicitThreshold)),
                    !!parameters.moderationParameters?.politicalThreshold && el(".step-param",
                        "Political", el("span", parameters.moderationParameters?.politicalThreshold)),
                    !!parameters.moderationParameters?.dangerousContentThreshold && el(".step-param",
                        "Dangerous content", el("span", parameters.moderationParameters?.dangerousContentThreshold)),
                    !!parameters.moderationParameters?.jailbreakThreshold && el(".step-param",
                        "Jailbreak attempt", el("span", parameters.moderationParameters?.jailbreakThreshold)),
                )
            ),
            el(".pipeline-column",
                el(".column-title", Icon("settings-3-line"), "Pre-processing"),
                el(".pipeline-step",
                    { dataState: !!parameters.knownToolHandlerName },
                    el(".step-title", "Tool handler")
                ),
                el(".pipeline-step",
                    { dataState: Object.keys(parameters.multimodalResolverParameters ?? {}).length > 0 },
                    el(".step-title", "Multimodal processing"),
                    Object.entries(parameters.multimodalResolverParameters ?? {}).map(([type, engine]) =>
                        el(".step-param", type.replace("Engine", ""), el("span", engine))
                    )
                )
            ),
            el(".pipeline-column",
                el(".column-title", Icon("box-1-line"), "Worker"),
                el(".pipeline-step",
                    { dataState: parameters.workerScriptSource != null },
                    el(".step-title", "Gateway worker")
                )
            ),
            el(".pipeline-column",
                el(".column-title", Icon("box-3-line"), "Inference"),
                el(".pipeline-step",
                    { dataState: !!parameters.modelName },
                    el(".step-title", parameters.modelName || "no-model")
                )
            ),
            el(".pipeline-column",
                el(".column-title", "Output"),
                el(".pipeline-step.hidden-step",
                    { dataState: true }
                ),
            ),
        )
    )
}
